import { useEffect, useRef, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";

import { getStationStateHubUrl } from "../services/StationStateApi";

export type LiveStatus = "connecting" | "connected" | "reconnecting" | "disconnected";

/**
 * Atualização em tempo real via SignalR para as telas de relatório.
 *
 * Assina o hub `/hubs/station-state` (o mesmo do semáforo) e dispara `onRefresh` a cada
 * `StateChanged` (novo evento interpretado), ao (re)conectar e no snapshot de conexão —
 * substituindo botões de "atualizar" e polling. Os pushes são debounced (~1,2 s) para
 * coalescer rajadas. `enabled=false` desliga (ex.: janela histórica, que não muda).
 *
 * O callback é lido via ref, então a assinatura não re-inscreve a cada render — sempre
 * chama a versão mais recente de `onRefresh` (com os filtros atuais).
 */
export const useLiveRefresh = (onRefresh: () => void, enabled: boolean = true): LiveStatus => {
  const cbRef = useRef(onRefresh);
  useEffect(() => {
    cbRef.current = onRefresh;
  }, [onRefresh]);

  const [status, setStatus] = useState<LiveStatus>("connecting");

  useEffect(() => {
    if (!enabled) {
      setStatus("disconnected");
      return;
    }

    let disposed = false;
    let debounce: number | undefined;
    const fire = (): void => {
      if (debounce) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        if (!disposed) cbRef.current();
      }, 1200);
    };

    let conn: HubConnection;
    try {
      conn = new HubConnectionBuilder()
        .withUrl(getStationStateHubUrl())
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();
    } catch {
      setStatus("disconnected");
      return;
    }

    conn.on("StateChanged", fire);
    conn.onreconnecting(() => setStatus("reconnecting"));
    conn.onreconnected(() => {
      setStatus("connected");
      fire();
    });
    conn.onclose(() => {
      if (!disposed) setStatus("disconnected");
    });

    setStatus("connecting");
    conn
      .start()
      .then(() => {
        if (!disposed) setStatus("connected");
      })
      .catch(() => {
        if (!disposed) setStatus("disconnected");
      });

    return () => {
      disposed = true;
      if (debounce) window.clearTimeout(debounce);
      if (conn.state !== HubConnectionState.Disconnected) conn.stop().catch(() => {});
    };
  }, [enabled]);

  return status;
};

export default useLiveRefresh;
